import { CommonModule } from '@angular/common'
import type {
  ElementRef,
  OnChanges,
  SimpleChanges,
  TemplateRef,
} from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  input,
  output,
  viewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'

import { FormatAuthorNamePipe } from '../../../../shared/pipes/format-author-name.pipe'
import { SharedModule } from '../../../../shared/shared.module'
import type { FilterItem } from '../../../../types/types'
import { FilterItemComponent } from '../filter-item/filter-item.component'

@Component({
  selector: 'dhp-generic-filter',
  templateUrl: './generic-filter.component.html',
  styleUrls: ['./generic-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    SharedModule,
    FilterItemComponent,
    FormatAuthorNamePipe,
  ],
})
export class GenericFilterComponent<T extends FilterItem> implements OnChanges {
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() items: T[] | null = []
  readonly isOpen = input(false)
  readonly selectedCount = input<number | null>(0)
  readonly isLoading = input(false)
  readonly searchTerm = input<string | null>('')
  readonly title = input('')
  readonly placeholder = input('')
  readonly showSearch = input(false)
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() customItemTemplate: TemplateRef<{ item: T }> | null = null

  readonly toggleDialog = output()
  readonly toggleSelection = output<T>()
  readonly clearSelection = output()
  readonly searchTermChange = output<string>()
  readonly scrolled = output()

  // Both elements only exist in the DOM while the dialog is open (behind
  // an @if block), so they must be optional, not required: `ngOnChanges`
  // reads them synchronously as soon as `isOpen` flips to true, before the
  // template has rendered that block for the first time.
  readonly scrollContainer =
    viewChild<ElementRef<HTMLDivElement>>('scrollContainer')
  readonly searchInput = viewChild<
    ElementRef<HTMLInputElement>
    // Used to prevent toggling the dialog when clicking inside the dialog
  >('searchInput')

  // Used to prevent toggling the dialog when clicking inside the dialog
  isDialogClicked = false

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    // Mark that the click happened inside our component
    event.stopPropagation()
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset scroll position when the filter is opened.
    // The scroll container only exists once the dialog's @if block has
    // rendered, which hasn't happened yet at this point in the CD cycle -
    // defer to let the template catch up first.
    if (changes['isOpen']?.currentValue === true) {
      setTimeout(() => {
        this.scrollToTop()
      }, 0)
      if (this.showSearch()) {
        setTimeout(() => {
          const searchInput = this.searchInput()
          if (searchInput?.nativeElement) {
            searchInput.nativeElement.focus()
          }
        }, 100)
      }
    }
  }

  trackById(_: number, item: T): string | number {
    return item.id
  }

  clearSearch(): void {
    this.searchTermChange.emit('')
  }

  onScroll(event: Event): void {
    const { scrollTop, scrollHeight, clientHeight } =
      event.target as HTMLElement
    // Emit when scrolled to bottom (with a 20px buffer)
    if (scrollHeight - scrollTop - clientHeight < 20) {
      // TODO: The 'emit' function requires a mandatory void argument
      this.scrolled.emit()
    }
  }

  scrollToTop(): void {
    const scrollContainer = this.scrollContainer()
    if (scrollContainer?.nativeElement) {
      scrollContainer.nativeElement.scrollTop = 0
    }
  }

  // Handle item selection
  onItemSelected(item: T): void {
    // Only emit to parent for state management
    this.toggleSelection.emit(item)
  }
}
